import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT, get } from "@insite/client-framework/Services/ApiService";
import { getBillTo } from "@insite/client-framework/Services/CustomersService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";

export interface CertificateModel {
    uuid: string;
    certificateId: number;
    replacedById: number | null;
    createdDateTime: string;
    startDateTime: Date | null;
    endDateTime: Date | null;
    class: string;
    status: "Approved" | "Invalid" | "Pending" | "Rejected" | string;
    source: string;
    reason: string;
    scope: string;
    taxResult: string;
    transactionTypes: string[];
    customFields: { id: number; label: string; value: string }[];
    contact: { uuid: string };
    customer: { uuid: string; code: string; name: string };
    seller: { uuid: string; code: string; name: string };
    coverages: string;
}

export interface PaginationModel {
    page: number;
    pageSize: number;
    totalItemCount: number;
    numberOfPages: number;
    nextPageUri: string | null;
    prevPageUri: string | null;
    pageSizeOptions?: number[];
}

export interface GetCertificatesResponse {
    certificates: CertificateModel[];
    pagination: PaginationModel;
}

interface Parameter {
    page?: number;
    pageSize?: number;
}

type HandlerType = Handler<Parameter, { apiResult: GetCertificatesResponse }>;

export const DispatchBeginLoadCertificates: HandlerType = props => {
    props.dispatch({
        type: "Widgets/ExemptionCertificateList/BeginLoadCertificates",
    });
};

export const RequestCertificates: HandlerType = async props => {
    try {
        // Prefer the current bill-to already loaded into Redux (the wizard calls loadCurrentBillTo),
        // so the post-upload poll (up to 12 times / 3s) doesn't re-fetch unchanging data every pass.
        // Fall back to the API only when it isn't in the store yet.
        const state = props.getState();
        const billToId = getSession(state).billToId;
        let customerCode = billToId ? getBillToState(state, billToId).value?.customerNumber : undefined;

        if (!customerCode) {
            const billTo = await getBillTo({ billToId: API_URL_CURRENT_FRAGMENT });
            customerCode = billTo.customerNumber;
        }

        if (!customerCode) {
            props.dispatch({
            type: "Widgets/ExemptionCertificateList/FailLoadCertificates",
                error: "Customer code is required to load exemption certificates",
            });
            return false;
        }

        const page = props.parameter.page || 1;
        const pageSize = props.parameter.pageSize || 10;

        props.apiResult = await get<GetCertificatesResponse>(
            "/api/v1/vertex/exemptioncertificates",
            {
                Page: page,
                PageSize: pageSize,
                CustomerCode: customerCode,
            } as any,
        );
    } catch (error) {
        props.dispatch({
            type: "Widgets/ExemptionCertificateList/FailLoadCertificates",
            error: error instanceof Error ? error.message : "Failed to load certificates",
        });
        return false;
    }
};

export const DispatchCompleteLoadCertificates: HandlerType = props => {
    props.dispatch({
        type: "Widgets/ExemptionCertificateList/CompleteLoadCertificates",
        result: props.apiResult,
    });
};

export const chain = [
    DispatchBeginLoadCertificates,
    RequestCertificates,
    DispatchCompleteLoadCertificates,
];

const loadExemptionCertificates = createHandlerChainRunner(chain, "LoadExemptionCertificates");
export default loadExemptionCertificates;
